/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useEffect, useState, useRef } from 'react';
import { retrieve, store } from '../utils/storageHelper';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';
import envConfig from '../config/envConfig';
import APIHelper from '../api/APIHelper';
import ProgressBarDialog from './common/ProgressBar';
import AlertDialog from './common/AlertDialog';
import ThemeSelectionModal from './common/ThemeSelectionModal';


const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret,
});


function DashboardHeader() {
    const [user, setUser] = useState(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const navigate = useNavigate();
    const { theme } = useTheme();
    const dropdownRef = useRef(null);
    const [loggingOut, setLoggingOut] = useState(false);
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);
    const [errorDialogMessage, setErrorDialogMessage] = useState("An error occurred while logging out.");
    const [isThemeModalOpen, setThemeModalOpen] = useState(false);

    useEffect(() => {
        const userData = retrieve('userData');
        if (userData) {
            setUser(userData);
        }
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dropdownRef]);

    const handleLogout = async () => {
        // prevent double clicks
        if (loggingOut) return;

        setLoggingOut(true);

        try {
            const response = await apiHelper.apiCall("/arcee/user/logout/", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
            });

            if (!response.ok) {
                let body = null;
                try { body = await response.json(); } catch (e) { body = null; }

                const message =
                    (body && (body.error || body.message || body.detail || JSON.stringify(body))) ||
                    `Logout failed (status ${response.status})`;
                setErrorDialogMessage(message);
                setErrorDialogOpen(true);

                setLoggingOut(false);
                return;
            }

            // Success — clear client-side state
            store('userData', null);
            store('access_token', null);

            // Navigate to sign-in page
            navigate('/sign-in', { replace: true });
        } catch (err) {
            console.error("Logout error:", err);

            let message = "Logout failed. Please try again.";
            if (err && err.type === "session_expired") {
                message = "Your session has expired. Please login again.";
            } else if (err && err.message) {
                message = err.message;
            }

            setErrorDialogMessage(message);
            setErrorDialogOpen(true);
        } finally {
            setLoggingOut(false);
        }
    }

    const toggleDropdown = () => {
        setDropdownOpen(!dropdownOpen);
    };

    const rawUserName = user?.user_name || (user ? 'User' : 'Guest');
    const headerDisplayName =
        rawUserName && rawUserName.length > 5
            ? `${rawUserName.slice(0, 5)}…`
            : rawUserName;

    return (
        <header className="d-flex justify-content-between align-items-center p-3 mb-4"
            style={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
            }}>
            <div>
                <h4 className="m-0 fw-bold" style={{ color: theme.primary }}>{envConfig.appName || ""}</h4>
            </div>

            <div className="d-flex align-items-center gap-4">
                {/* Icons Section */}
                <div className="d-flex gap-3">
                    <div className="position-relative d-none" style={{ cursor: 'pointer' }}>
                        <i className="bi bi-chat-text fs-5 text-secondary"></i>
                        <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: '0.6rem' }}>
                            3
                        </span>
                    </div>
                    <div className="position-relative d-none" style={{ cursor: 'pointer' }}>
                        <i className="bi bi-bell fs-5 text-secondary"></i>
                        <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: '0.6rem' }}>
                            5
                        </span>
                    </div>
                </div>

                {/* Separator */}
                <div style={{ width: 1, height: 24, background: '#e0e0e0' }}></div>

                {/* User Profile Dropdown */}
                <div className="position-relative" ref={dropdownRef}>
                    <div
                        className="d-flex align-items-center gap-2"
                        style={{ cursor: 'pointer' }}
                        onClick={toggleDropdown}
                    >
                        <div style={{ textAlign: 'right', lineHeight: 1.2 }}>
                            <div
                                className="fw-bold"
                                style={{
                                    fontSize: '0.9rem',
                                    color: theme.primary,
                                    maxWidth: 120,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}
                                title={rawUserName}
                            >
                                {headerDisplayName}
                            </div>
                            <small className="text-muted" style={{ fontSize: '0.75rem' }}>Admin</small>
                        </div>
                        <div
                            className="d-flex justify-content-center align-items-center text-white fw-bold"
                            style={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`
                            }}
                        >
                            {user && (user.user_name ? user.user_name[0].toUpperCase() : 'U')}
                        </div>
                    </div>

                    {/* Dropdown Menu */}
                    {dropdownOpen && (
                        <div
                            className="position-absolute end-0 mt-2 bg-white rounded shadow-sm border"
                            style={{ width: 180, zIndex: 1000, overflow: 'hidden' }}
                        >
                            <div className="px-3 py-2 border-bottom">
                                <span className="d-block small text-muted">Signed in as</span>
                                <strong className="text-truncate d-block">{user?.user_name || user?.username || 'User'}</strong>
                            </div>
                            <button
                                className="dropdown-item py-2 px-3 d-flex align-items-center gap-2 w-100 text-start bg-white border-0 hover-bg-light"
                                onClick={() => {
                                    navigate(`/dashboard/Profile`);
                                }}
                            >
                                <i className="bi bi-person"></i> Profile
                            </button>
                            <button
                                onClick={() => setThemeModalOpen(true)}
                                className="dropdown-item py-2 px-3 d-flex align-items-center gap-2 w-100 text-start bg-white border-0 hover-bg-light">
                                <i className="bi bi-palette"></i> Switch Theme
                            </button>
                            <button className="dropdown-item py-2 px-3 d-flex align-items-center gap-2 w-100 text-start bg-white border-0 hover-bg-light">
                                <i className="bi bi-gear"></i> Settings
                            </button>
                            <div className="dropdown-divider m-0"></div>
                            <button
                                onClick={handleLogout}
                                className="dropdown-item py-2 px-3 d-flex align-items-center gap-2 w-100 text-start text-danger bg-white border-0 hover-bg-light"
                            >
                                <i className="bi bi-box-arrow-right"></i> Logout
                            </button>
                        </div>
                    )}
                </div>
            </div>
            <ProgressBarDialog
                open={loggingOut}
                text_message="Logging out..."
                cancellable={false}
            />

            {/* Error dialog - using your AlertDialog component */}
            <AlertDialog
                open={errorDialogOpen}
                title="Logout failed"
                message={errorDialogMessage}
                type="error"
                okLabel="OK"
                showCancel={false}
                onOk={() => setErrorDialogOpen(false)}
                onCancel={() => setErrorDialogOpen(false)}
                mini_mode={true}
            />
            <ThemeSelectionModal
                open={isThemeModalOpen}
                onClose={() => setThemeModalOpen(false)}
            />
        </header>
    );
}

export default DashboardHeader;
